import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const gpuApi = createApi({
  reducerPath: "gpuApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().user.token;
      if (token) {
        headers.set("authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["GPU"],
  endpoints: (builder) => ({
    // Get GPU list with pagination and search
    getGpuList: builder.query({
      query: ({ limit = 10, offset = 0, search = "", allFields = true }) => ({
        url: "/api/v1/gpu-summary/",
        params: { limit, offset, search, allFields },
      }),
      transformResponse: (response, meta, arg) => {
        return {
          data: response.data,
          pagination: {
            total: response.total,
            limit: arg.limit,
            offset: arg.offset,
          },
        };
      },
      providesTags: ["GPU"],
    }),

    // Get single GPU detail
    getGpuDetail: builder.query({
      query: (id) => ({
        url: `/api/v1/gpu-detail/${id}`,
        params: { allFields: true },
      }),
      transformResponse: (response) => response.data,
      providesTags: ["GPU"],
    }),

    // Get list of available GPU instances
    getGpuInstanceList: builder.query({
      query: () => ({
        url: "/api/v1/gpu-instance-list",
      }),
      transformResponse: (response) => response.data || [],
      providesTags: ["GPU"],
    }),

    // Get GPU type by instance_type
    getGpuType: builder.query({
      query: (instance_type) => ({
        url: "/api/v1/gpu-types/",
        params: { instance_type },
      }),
      transformResponse: (response) => response.data,
      // Don't cache this separately - fetch fresh each time for form dropdowns
    }),

    // Search GPUs with filters
    searchGpu: builder.query({
      query: ({ gpu_type, instance_type, gpuname, limit = 10, offset = 0 }) => {
        const params = { limit, offset };
        if (gpu_type) params.gpu_type = gpu_type;
        if (instance_type) params.instance_type = instance_type;
        if (gpuname) params.gpuname = gpuname;
        return {
          url: "/api/v1/gpu-search/",
          params,
        };
      },
      transformResponse: (response) => {
        return {
          data: response.data,
          total: response.total,
        };
      },
      providesTags: ["GPU"],
    }),

    // Create GPU
    createGpu: builder.mutation({
      query: (data) => ({
        url: "/api/v1/gpu-create/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GPU"],
    }),

    // Update GPU
    updateGpu: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/gpu-edit/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["GPU"],
    }),

    // Delete GPU
    deleteGpu: builder.mutation({
      query: (id) => ({
        url: `/api/v1/gpu-delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["GPU"],
    }),
  }),
});

export const {
  useGetGpuListQuery,
  useGetGpuDetailQuery,
  useGetGpuInstanceListQuery,
  useGetGpuTypeQuery,
  useSearchGpuQuery,
  useCreateGpuMutation,
  useUpdateGpuMutation,
  useDeleteGpuMutation,
} = gpuApi;
